import { getDistanceSortedMissionCards, getMissionListingKey, matchesMissionTypeFilter } from '../../../models/helpers/missionListingHelper';
import CompactMissionFilters from './CompactMissionFilters';
import MissionPromotionBanner from './MissionPromotionBanner';
import AgeRestrictedMissionIcon from './AgeRestrictedMissionIcon';
import { AGE_RESTRICTED_BANNER_COLOR, AGE_RESTRICTED_DARK_COLOR, AGE_RESTRICTED_PRIMARY_COLOR } from '../../map/ageRestrictedPin';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FlatList,
  Image,
  NativeSyntheticEvent,
  PanResponder,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  View,
  ViewProps,
  useWindowDimensions,
} from 'react-native';
import useCustomTheme, { CustomTheme } from '../../../hooks/theme-hook';
import { MissionFilter } from '../../../models/MapModels';
import { FilterType } from '../../../models/MissionFilterType';
import MissionCard, { AirportMissionSearchContext, MissionCardProps } from './MissionCard';
import MapFilter from '../filter/MapFilter';
import { FilterItemProps } from '../filter/FilterItem';
import Label, { LabelType } from '../Label';
import { StylesHelper } from '../../../models/helpers/stylesHelper';
import useTranslation from '../../../hooks/translation-hook';
import MoreInfoButton from '../MoreInfoButton';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faFilterSlash, faChevronDown, faChevronLeft, faChevronRight, faPlaneDeparture } from '@fortawesome/pro-regular-svg-icons';
import Loading from '../Loading';
import useStyles from '../../../hooks/styles-hook';
import {
  AirportVisitType as ApiAirportVisitType,
  GetAirportMissionZoneDto,
  GetMissionsFromMapDto,
  GetMissionsTagDto,
  MissionsClient,
  MissionType,
} from '../../../api/api.g';
import { MapLocation } from '../../../models/MapModels';
import CustomButton from '../CustomButton';
import moment from 'moment';
import CustomDatePicker from '../CustomDatePicker';
import CustomTimePicker from '../CustomTimePicker';
import airplaneFlyingIcon from '../../../../assets/aiplane-flying.png';
import gateIcon from '../../../../assets/gate-icon.png';
import editIcon from '../../../../assets/edit.png';
import airplaneTopMenu from '../../../../assets/airplane-top-menu.png';
import airportBannerListIcon from '../../../../assets/airport-banner-list.png';
import airportLocationLogo from '../../../../assets/airports/yul.png';
import useApi from '../../../hooks/api-hook';
import { useAppSelector } from '../../../hooks/store-hook';
import { MissionsHelper } from '../../../models/helpers/missionsHelper';
import { getMissionDiscoveryAvailability, isMissionAvailableForDiscovery } from '../../../models/helpers/missionAvailabilityHelper';

export interface MissionListProps extends ViewProps {
  airportMissionCards: MissionCardProps[];
  pinnedMissionCards: MissionCardProps[];
  compactFiltersExpanded?: boolean;
  onCompactFiltersExpandedChange?(expanded: boolean): void;
  missionCards: MissionCardProps[];
  flashMissionCards: MissionCardProps[];
  drawerVersion?: boolean;
  showAsCards?: boolean;
  selected?: MissionFilter;
  loading?: MissionCardProps;
  loadingSpin?: boolean;
  filter?: FilterType;
  onFilterChange?: (filter: FilterType) => void;
  onAirportBannerPress?: (branchId: string) => void;
  showAgeRestrictedPromotion?: boolean;
  airportBookingLocation?: MapLocation;
  onAirportBookingVisibilityChange?: (isVisible: boolean) => void;
  onAirportStartDateChange?: (startDate?: Date) => void;
  autoOpenAirportBookingKey?: string;
  autoOpenAirportBookingBranchId?: string;
  autoOpenAirportBookingReturnToListView?: boolean;
  resetAirportFlowKey?: string;
  onAirportBookingExitToOrigin?: (returnToListView: boolean) => void;
  onAutoOpenAirportBookingHandled?: (key: string) => void;
  /** When true, list is fed by map; use map filter state and show same missions as map. */
  syncWithMapFilters?: boolean;
  /** Map's filter options (from API tags). When provided with syncWithMapFilters, list uses these instead of mainFilterItems. */
  mapFilterItems?: FilterItemProps[];
  /** Map's acceptable/upcoming state; used when syncWithMapFilters. */
  mapOnlyAcceptableMissions?: boolean;
  mapShowUpcomingMissions?: boolean;
  /** Called when user changes business-type selection in list; updates map so both stay in sync. */
  onMapFilterSelectionChange?(selectedIds: string[]): void;
  onMapOnlyAcceptableMissionsChange?(value: boolean): void;
  onMapShowUpcomingMissionsChange?(value: boolean): void;
  onMissionListRefresh?(): Promise<void> | void;
}

type AirportConnectionCode = string | typeof AIRPORT_CONNECTION_NONE;
type AirportFlowStep = 'purpose' | 'route' | 'list';
type MissionListRow =
  | { type: 'sectionHeader'; id: string; title: string }
  | { type: 'missionCard'; id: string; card: MissionCardProps; pinned?: boolean }
  | { type: 'airportEntry'; id: string; card: MissionCardProps }
  | { type: 'empty'; id: string };

const AIRPORT_CONNECTION_NONE = 'NONE';
const AIRPORT_FLOW_NATIVE_PAGES: Record<AirportFlowStep, number> = {
  purpose: 1,
  route: 2,
  list: 3,
};
const AIRPORT_FLOW_IOS_PROGRAMMATIC_PAGE_ANIMATION_ENABLED = false;
const mainMissionHeaderReservedHeight = 72;
const airportDateFormats = ['YYYY-MM-DD', 'YYYY/MM/DD'];
const airportRouteInputFieldStyle = {
  backgroundColor: '#ffffff',
  borderColor: '#9bc3df',
};
const airportRouteInputValueTextColor = '#102334';
const airportRouteInputPlaceholderTextColor = '#6f8598';
const airportRouteInputIconColor = '#1f78c8';
const airportListBackgroundColorFixed = '#f3f3f3';
const airportListAccentColorFixed = '#1f78c8';
const airportListTitleColorFixed = '#0a4f84';
const airportListTextColorFixed = '#102334';
const airportListCardBackgroundColorFixed = '#d8eaf7';
const airportListCardInnerBackgroundColorFixed = '#ecf6ff';
const airportListBorderColorFixed = '#c5ddef';
const airportListLogoBackgroundColorFixed = '#ffffff';
const airportListErrorColorFixed = '#c43d5a';
const airportRouteBackgroundColorFixed = '#d8e8f2';

const airportVisitTypeUsesPurposeDateTime = (value?: ApiAirportVisitType) =>
  value === ApiAirportVisitType.PickupDropoff || value === ApiAirportVisitType.Arriving;

const getAirportBusinessType = (card: MissionCardProps) => {
  const value = (card.industry ?? '')
    .split(',')
    .map((x) => x.trim())
    .find((x) => x.length > 0);

  return value && value.length > 0 ? value : 'N/A';
};

const isPublicAirportZoneMission = (card: MissionCardProps) => card.branchAirportInfo?.isRestrictedArea === false;

const sortPublicAirportZoneLast = (cards: MissionCardProps[]) => {
  return (cards ?? [])
    .map((card, index) => ({ card, index }))
    .sort((left, right) => {
      const leftPublicZone = isPublicAirportZoneMission(left.card);
      const rightPublicZone = isPublicAirportZoneMission(right.card);

      if (leftPublicZone !== rightPublicZone) {
        return leftPublicZone ? 1 : -1;
      }

      return left.index - right.index;
    })
    .map((item) => item.card);
};

const getAirportOnlyAcceptableDetailValue = (onlyAcceptableMissions: boolean, showUpcomingMissions: boolean) =>
  showUpcomingMissions ? false : onlyAcceptableMissions;

const MissionList = (props: MissionListProps) => {
  const theme: CustomTheme = useCustomTheme();
  const t = useTranslation();
  const styles = useStyles((x) => x.missionsList);
  const layout = useWindowDimensions();
  const apiMissions = useApi(MissionsClient);
  const uiLanguage = useAppSelector((selector) => selector.userPrefs.uiLanguage);
  const selected = props.selected;
  const loading = props.loading;
  const [localMissionFilter, setLocalMissionFilter] = useState(FilterType.All);
  const missionFilter = props.filter ?? localMissionFilter;
  const setMissionFilter = (next: FilterType) => {
    setLocalMissionFilter(next);
    props.onFilterChange?.(next);
  };
  const mainMissionListRef = useRef<FlatList<MissionListRow>>(null);
  const [airportListOpen, setAirportListOpen] = useState(false);
  const [airportBookingBranchId, setAirportBookingBranchId] = useState<string | undefined>();
  const [airportBookingReturnToListView, setAirportBookingReturnToListView] = useState(true);
  const [contentWidth, setContentWidth] = useState(0);
  const pagingRef = useRef<ScrollView>(null);
  const airportFlowUserScrollRef = useRef(false);
  const airportFlowDragStartXRef = useRef(0);
  const airportFlowDragStartPageRef = useRef<number | undefined>(undefined);
  const airportFlowProgrammaticTargetPageRef = useRef<number | undefined>(undefined);
  const lastAutoOpenAirportKeyRef = useRef<string | undefined>(undefined);
  const lastResetAirportFlowKeyRef = useRef<string | undefined>(undefined);
  const airportRouteScrollRef = useRef<ScrollView>(null);
  const airportZonesRequestRef = useRef(0);
  const airportMissionsRequestRef = useRef(0);
  const skipNextAirportToggleRefreshRef = useRef(false);
  const previousUiLanguageRef = useRef<string | undefined>(uiLanguage);
  const [airportFlowStep, setAirportFlowStep] = useState<AirportFlowStep>('purpose');
  const [airportDepartureDate, setAirportDepartureDate] = useState('');
  const [airportDepartureTime, setAirportDepartureTime] = useState('');
  const [airportDestination, setAirportDestination] = useState<string | undefined>();
  const [airportConnectionDestination, setAirportConnectionDestination] = useState<AirportConnectionCode | undefined>();
  const [airportRouteError, setAirportRouteError] = useState<string | undefined>();
  const [airportApiError, setAirportApiError] = useState<string | undefined>();
  const [airportRouteViewportHeight, setAirportRouteViewportHeight] = useState(0);
  const [airportRouteContentHeight, setAirportRouteContentHeight] = useState(0);
  const [airportRouteScrollY, setAirportRouteScrollY] = useState(0);
  const [airportZones, setAirportZones] = useState<GetAirportMissionZoneDto[]>([]);
  const [airportZonesLoading, setAirportZonesLoading] = useState(false);
  const [airportApiMissionCards, setAirportApiMissionCards] = useState<MissionCardProps[]>([]);
  const [airportPersistentApiTags, setAirportPersistentApiTags] = useState<GetMissionsTagDto[]>([]);
  const [airportSelectedTagIds, setAirportSelectedTagIds] = useState<string[]>([]);
  const [airportFilterItems, setAirportFilterItems] = useState<FilterItemProps[]>([]);
  const [airportFilterOpen, setAirportFilterOpen] = useState(false);
  const [airportMissionsLoading, setAirportMissionsLoading] = useState(false);
  const [airportBaseSearchHasResults, setAirportBaseSearchHasResults] = useState<boolean | undefined>(undefined);
  const [airportOnlyAcceptableMissions, setAirportOnlyAcceptableMissions] = useState(false);
  const [airportShowUpcomingMissions, setAirportShowUpcomingMissions] = useState(true);
  const [mainFilterItems, setMainFilterItems] = useState<FilterItemProps[]>([]);
  const [mainFilterOpen, setMainFilterOpen] = useState(false);
  const [mainOnlyAcceptableMissions, setMainOnlyAcceptableMissions] = useState(false);
  const [mainShowUpcomingMissions, setMainShowUpcomingMissions] = useState(true);
  const [isPullRefreshing, setIsPullRefreshing] = useState(false);
  const [airportVisitType, setAirportVisitType] = useState<ApiAirportVisitType | undefined>();
  const [airportPurposeDate, setAirportPurposeDate] = useState('');
  const [airportPurposeTime, setAirportPurposeTime] = useState('');
  const [airportPurposeError, setAirportPurposeError] = useState<string | undefined>();

  const isMobileMissionList = layout.width <= 1200;
  const isWeb = Platform.OS === 'web';
  const isNativeMobile = !isWeb;
  const isAirportDarkMode = !!theme.isDark;
  const airportDepartureMinDate = moment().startOf('day');
  const airportDepartureMaxDate = moment().add(12, 'months').startOf('day');
  const getAirportVisitTypeLabel = (value?: ApiAirportVisitType) => {
    switch (value) {
      case ApiAirportVisitType.PickupDropoff:
        return t('airport-purpose-pickup-dropoff');
      case ApiAirportVisitType.Arriving:
        return t('airport-purpose-arriving');
      case ApiAirportVisitType.Departing:
        return t('airport-purpose-departing');
      default:
        return '';
    }
  };
  const airportVisitTypeOptions: { value: ApiAirportVisitType; label: string }[] = [
    { value: ApiAirportVisitType.Departing, label: getAirportVisitTypeLabel(ApiAirportVisitType.Departing) },
    { value: ApiAirportVisitType.Arriving, label: getAirportVisitTypeLabel(ApiAirportVisitType.Arriving) },
    { value: ApiAirportVisitType.PickupDropoff, label: getAirportVisitTypeLabel(ApiAirportVisitType.PickupDropoff) },
  ];
  const getAirportPurposeDateLabel = () => (airportVisitType === ApiAirportVisitType.Arriving ? t('airport-purpose-arrival-date') : t('airport-list-date'));
  const getAirportPurposeTimeLabel = () => (airportVisitType === ApiAirportVisitType.Arriving ? t('airport-purpose-arrival-time') : t('airport-booking-time'));
  const formatAirportPurposeFieldError = (fieldLabel: string, message: string) => `${fieldLabel}: ${message}`;

  const airportMissionCards = useMemo(() => props.airportMissionCards ?? [], [props.airportMissionCards]);

  const nonAirportPinnedMissionCards = useMemo(
    () => (props.pinnedMissionCards ?? []).filter((card) => card.missionType !== MissionType.Airport),
    [props.pinnedMissionCards]
  );
  const hasAirportMissions = useMemo(() => airportMissionCards.length > 0, [airportMissionCards]);
  const hasAirportBookingFlow = !!airportBookingBranchId;
  const useCompactIosAirportPages = Platform.OS === 'ios' && airportVisitType !== ApiAirportVisitType.Departing;
  const resetAirportFlowDragTracking = () => {
    airportFlowUserScrollRef.current = false;
    airportFlowDragStartXRef.current = 0;
    airportFlowDragStartPageRef.current = undefined;
  };
  const scrollAirportFlowToNativePage = (page: number, animated: boolean = true) => {
    if (isWeb || contentWidth <= 0) {
      return;
    }

    const shouldAnimate = animated && (Platform.OS !== 'ios' || AIRPORT_FLOW_IOS_PROGRAMMATIC_PAGE_ANIMATION_ENABLED);

    if (Platform.OS === 'ios') {
      if (shouldAnimate && airportFlowProgrammaticTargetPageRef.current === page) {
        return;
      }

      resetAirportFlowDragTracking();
      airportFlowProgrammaticTargetPageRef.current = shouldAnimate ? page : undefined;
    }

    pagingRef.current?.scrollTo({ x: contentWidth * page, animated: shouldAnimate });
  };
  const getAirportNativePage = (step: AirportFlowStep) => {
    if (useCompactIosAirportPages && step === 'list') {
      return AIRPORT_FLOW_NATIVE_PAGES.route;
    }

    return AIRPORT_FLOW_NATIVE_PAGES[step];
  };
  const airportZoneNameById = useMemo(() => {
    const zoneLookup = new Map<string, string>();
    (airportZones ?? []).forEach((zone) => {
      if (zone.zoneId) {
        zoneLookup.set(zone.zoneId, zone.name ?? '-');
      }
    });
    return zoneLookup;
  }, [airportZones]);
  const airportRouteOptions = useMemo(() => (airportZones ?? []).filter((zone) => !!zone.zoneId), [airportZones]);
  const airportConnectionRouteOptions = useMemo<AirportConnectionCode[]>(
    () => [AIRPORT_CONNECTION_NONE, ...airportRouteOptions.map((zone) => zone.zoneId as string)],
    [airportRouteOptions]
  );
  const formatAirportRouteOptionLabel = (label?: string) => label?.trim() || '-';
  const getAirportRouteLabel = (code?: AirportConnectionCode, allowNone: boolean = false) => {
    if (!code) {
      return '-';
    }

    if (allowNone && code === AIRPORT_CONNECTION_NONE) {
      return t('airport-route-none');
    }

    return formatAirportRouteOptionLabel(airportZoneNameById.get(code));
  };
  const airportZoneCode = airportConnectionDestination === AIRPORT_CONNECTION_NONE ? airportDestination : (airportConnectionDestination ?? airportDestination);
  const getAirportMissionZoneLabel = (card: MissionCardProps) => {
    const zone = card.branchAirportInfo?.zoneName?.trim() || getAirportRouteLabel(airportZoneCode);

    if (card.branchAirportInfo?.isRestrictedArea === false) {
      return zone;
    }

    return t('airport-list-zone-regulated', { zone });
  };
  const airportDestinationLabel = useMemo(() => getAirportRouteLabel(airportDestination), [airportDestination, airportZoneNameById, t]);
  const airportConnectionLabel = useMemo(
    () => getAirportRouteLabel(airportConnectionDestination, true),
    [airportConnectionDestination, airportZoneNameById, t]
  );

  useEffect(() => {
    mainMissionListRef.current?.scrollToOffset({ offset: 0, animated: false });
  }, [missionFilter]);

  useEffect(() => {
    if (props.loadingSpin && mainFilterOpen) {
      setMainFilterOpen(false);
    }
  }, [props.loadingSpin, mainFilterOpen]);

  useEffect(() => {
    if ((props.airportMissionCards?.length ?? 0) <= 1 && airportListOpen) {
      setAirportListOpen(false);
    }
  }, [props.airportMissionCards, airportListOpen]);

  useEffect(() => {
    if (isWeb || !contentWidth) {
      return;
    }

    const targetPage = airportBookingBranchId ? getAirportNativePage(airportFlowStep) : 0;
    scrollAirportFlowToNativePage(targetPage);
  }, [isWeb, airportBookingBranchId, airportFlowStep, contentWidth]);

  useEffect(() => {
    props.onAirportBookingVisibilityChange?.(hasAirportBookingFlow);
  }, [props.onAirportBookingVisibilityChange, hasAirportBookingFlow]);

  const getAirportApiErrorMessage = (error: any) => {
    if (error?.status === 400) {
      return t('field_value_not_valid');
    }

    return t('Something-went-wrong-please-try-again-later');
  };

  const buildAirportFilterItems = (tags: GetMissionsTagDto[], selectedTagIds: string[]): FilterItemProps[] => {
    return (tags ?? [])
      .filter((tag) => !!tag.id)
      .map((tag) => {
        const id = `${tag.id}`;
        return {
          id,
          name: tag.name === '' ? 'N/D' : (tag.name ?? 'N/D'),
          checked: selectedTagIds.includes(id),
        };
      });
  };

  const mergeAirportTags = (currentTags: GetMissionsTagDto[], nextTags: GetMissionsTagDto[]) => {
    const tagMap = new Map<string, GetMissionsTagDto>();

    (currentTags ?? []).forEach((tag) => {
      if (tag?.id) {
        tagMap.set(`${tag.id}`, tag);
      }
    });

    (nextTags ?? []).forEach((tag) => {
      if (tag?.id) {
        tagMap.set(`${tag.id}`, tag);
      }
    });

    return Array.from(tagMap.values());
  };

  const mapAirportMissionsToCards = (missions: GetMissionsFromMapDto[], airportSearchContext: AirportMissionSearchContext) => {
    const missionPressHandler = airportMissionCards[0]?.onPress;

    return (missions ?? []).map(
      (mission) =>
        ({
          ...MissionsHelper.mapToMissionCard(mission, (card) => {
            missionPressHandler?.(card);
          }),
          airportSearchContext,
        }) as MissionCardProps,
    );
  };

  const loadAirportZones = async (options?: { force?: boolean }) => {
    if (airportZonesLoading || (!options?.force && (airportZones?.length ?? 0) > 0)) {
      return;
    }

    const requestId = ++airportZonesRequestRef.current;
    setAirportZonesLoading(true);
    setAirportApiError(undefined);

    try {
      const response = await apiMissions.getAirportZones(apiMissions.tokenSource?.token);
      if (requestId !== airportZonesRequestRef.current) {
        return;
      }

      setAirportZones(response?.zones ?? []);
    } catch (error: any) {
      if (requestId === airportZonesRequestRef.current) {
        setAirportApiError(getAirportApiErrorMessage(error));
      }
    } finally {
      if (requestId === airportZonesRequestRef.current) {
        setAirportZonesLoading(false);
      }
    }
  };

  const fetchAirportMissions = async (options?: {
    selectedTagIds?: string[];
    openListOnSuccess?: boolean;
    onlyAcceptableMissions?: boolean;
    showUpcomingMissions?: boolean;
  }) => {
    const purposeValidationError = getAirportPurposeValidationError();
    if (purposeValidationError) {
      setAirportPurposeError(purposeValidationError);
      setAirportApiError(purposeValidationError);
      return false;
    }

    const shouldUseTravelRoute = airportVisitType === ApiAirportVisitType.Departing;
    if (shouldUseTravelRoute && (!airportDestination || !airportConnectionDestination || !airportSearchDateTime)) {
      const requiredMessage = t('airport-route-error-required');
      setAirportRouteError(requiredMessage);
      setAirportApiError(requiredMessage);
      return false;
    }

    const selectedTagIds = options?.selectedTagIds ?? airportSelectedTagIds;
    const onlyAcceptable = options?.onlyAcceptableMissions ?? airportOnlyAcceptableMissions;
    const showUpcoming = options?.showUpcomingMissions ?? airportShowUpcomingMissions;
    const isBaseAirportSearch = selectedTagIds.length <= 0 && !onlyAcceptable && showUpcoming;
    const connectionZoneId =
      shouldUseTravelRoute && airportConnectionDestination && airportConnectionDestination !== AIRPORT_CONNECTION_NONE
        ? airportConnectionDestination
        : undefined;
    const requestId = ++airportMissionsRequestRef.current;
    setAirportMissionsLoading(true);
    setAirportApiError(undefined);

    try {
      const result = await apiMissions.getAirportMissions(
        shouldUseTravelRoute ? airportDestination : undefined,
        connectionZoneId,
        airportMissionSearchDateTimeText,
        airportVisitType,
        selectedTagIds.length > 0 ? selectedTagIds : undefined,
        onlyAcceptable,
        showUpcoming,
        apiMissions.tokenSource?.token
      );

      if (requestId !== airportMissionsRequestRef.current) {
        return false;
      }

      const allMissions = [...(result?.highOrderMissions ?? []), ...(result?.missions ?? []), ...(result?.lowOrderMissions ?? [])];
      const nextMissionCards = mapAirportMissionsToCards(allMissions, {
        searchDateTime: airportMissionSearchDateTimeText,
        onlyAcceptableMissions: getAirportOnlyAcceptableDetailValue(onlyAcceptable, showUpcoming),
      });
      const nextTags = (result?.tags ?? []).filter((tag) => !!tag.id);
      const persistentTags = mergeAirportTags(airportPersistentApiTags, nextTags);
      const availableTagIds = new Set(persistentTags.map((x) => `${x.id}`));
      const normalizedSelectedTagIds = selectedTagIds.filter((id) => availableTagIds.has(`${id}`));

      setAirportApiMissionCards(nextMissionCards);
      setAirportPersistentApiTags(persistentTags);
      setAirportSelectedTagIds(normalizedSelectedTagIds);
      setAirportFilterItems(buildAirportFilterItems(persistentTags, normalizedSelectedTagIds));

      if (isBaseAirportSearch) {
        setAirportBaseSearchHasResults(nextMissionCards.length > 0);
      }

      if (options?.openListOnSuccess) {
        openAirportListStep();
      }

      return true;
    } catch (error: any) {
      if (requestId === airportMissionsRequestRef.current) {
        setAirportApiError(getAirportApiErrorMessage(error));
      }
      return false;
    } finally {
      if (requestId === airportMissionsRequestRef.current) {
        setAirportMissionsLoading(false);
      }
    }
  };

  const openAirportBookingInMainView = (branchId?: string, returnToListView: boolean = true) => {
    if (!branchId) {
      return;
    }

    const selectedAirportCard = airportMissionCards.find((card) => card.id === branchId);
    const nextAirportRouteOptions = (selectedAirportCard?.airportRouteOptions ?? []).filter((zone) => !!zone.zoneId);

    airportMissionsRequestRef.current += 1;
    airportZonesRequestRef.current += 1;
    setAirportBookingBranchId(branchId);
    setAirportBookingReturnToListView(returnToListView);
    setAirportFlowStep('purpose');
    setAirportVisitType(undefined);
    setAirportPurposeDate('');
    setAirportPurposeTime('');
    setAirportPurposeError(undefined);
    setAirportRouteError(undefined);
    setAirportApiError(undefined);
    setAirportApiMissionCards([]);
    setAirportPersistentApiTags([]);
    setAirportSelectedTagIds([]);
    setAirportFilterItems([]);
    setAirportBaseSearchHasResults(undefined);
    setAirportOnlyAcceptableMissions(false);
    setAirportShowUpcomingMissions(true);
    setAirportDestination(undefined);
    setAirportConnectionDestination(undefined);
    setAirportZones(nextAirportRouteOptions);
    setAirportZonesLoading(false);
    if (nextAirportRouteOptions.length > 0) {
      return;
    }
    void loadAirportZones({ force: true });
  };

  const closeAirportBookingInMainView = () => {
    airportMissionsRequestRef.current += 1;
    setAirportBookingBranchId(undefined);
    setAirportBookingReturnToListView(true);
    setAirportFlowStep('purpose');
    setAirportVisitType(undefined);
    setAirportPurposeDate('');
    setAirportPurposeTime('');
    setAirportPurposeError(undefined);
    setAirportRouteError(undefined);
    setAirportApiError(undefined);
    setAirportBaseSearchHasResults(undefined);
    setAirportMissionsLoading(false);
    setAirportDestination(undefined);
    setAirportConnectionDestination(undefined);
    setAirportZones([]);
    setAirportZonesLoading(false);
  };

  useEffect(() => {
    if (airportDestination && !airportZoneNameById.has(airportDestination)) {
      setAirportDestination(undefined);
    }

    if (airportConnectionDestination && airportConnectionDestination !== AIRPORT_CONNECTION_NONE && !airportZoneNameById.has(airportConnectionDestination)) {
      setAirportConnectionDestination(undefined);
    }
  }, [airportDestination, airportConnectionDestination, airportZoneNameById]);

  useEffect(() => {
    setAirportFilterItems(buildAirportFilterItems(airportPersistentApiTags, airportSelectedTagIds));
  }, [airportPersistentApiTags, airportSelectedTagIds]);

  const shouldShowAirportFilter = airportBaseSearchHasResults !== false;

  useEffect(() => {
    if (!shouldShowAirportFilter && airportFilterOpen) {
      setAirportFilterOpen(false);
    }
  }, [shouldShowAirportFilter, airportFilterOpen]);

  useEffect(() => {
    if (airportFlowStep !== 'list' || !airportBookingBranchId || airportVisitType === undefined) {
      return;
    }

    if (airportVisitType === ApiAirportVisitType.Departing && (!airportDestination || !airportConnectionDestination)) {
      return;
    }

    if (airportVisitTypeUsesPurposeDateTime(airportVisitType) && (!airportPurposeDate || !airportPurposeTime)) {
      return;
    }

    if (skipNextAirportToggleRefreshRef.current) {
      skipNextAirportToggleRefreshRef.current = false;
      return;
    }

    void fetchAirportMissions();
  }, [airportOnlyAcceptableMissions, airportShowUpcomingMissions]);

  useEffect(() => {
    if (previousUiLanguageRef.current === uiLanguage) {
      return;
    }

    previousUiLanguageRef.current = uiLanguage;

    if (!airportBookingBranchId) {
      return;
    }

    void loadAirportZones({ force: true });

    if (airportFlowStep !== 'list') {
      return;
    }

    if (airportVisitType === ApiAirportVisitType.Departing && (!airportDestination || !airportConnectionDestination)) {
      return;
    }

    if (airportVisitTypeUsesPurposeDateTime(airportVisitType) && (!airportPurposeDate || !airportPurposeTime)) {
      return;
    }

    void fetchAirportMissions({
      selectedTagIds: airportSelectedTagIds,
      onlyAcceptableMissions: airportOnlyAcceptableMissions,
      showUpcomingMissions: airportShowUpcomingMissions,
    });
  }, [
    uiLanguage,
    airportBookingBranchId,
    airportFlowStep,
    airportVisitType,
    airportPurposeDate,
    airportPurposeTime,
    airportDestination,
    airportConnectionDestination,
    airportSelectedTagIds,
    airportOnlyAcceptableMissions,
    airportShowUpcomingMissions,
  ]);

  useEffect(() => {
    const openKey = props.autoOpenAirportBookingKey;
    if (!openKey || lastAutoOpenAirportKeyRef.current === openKey) {
      return;
    }

    const branchIdToOpen = props.autoOpenAirportBookingBranchId ?? airportMissionCards?.[0]?.id;
    if (!branchIdToOpen) {
      return;
    }

    lastAutoOpenAirportKeyRef.current = openKey;
    openAirportBookingInMainView(branchIdToOpen, props.autoOpenAirportBookingReturnToListView ?? true);
    props.onAutoOpenAirportBookingHandled?.(openKey);
  }, [props.autoOpenAirportBookingKey, props.autoOpenAirportBookingBranchId, props.autoOpenAirportBookingReturnToListView, airportMissionCards]);

  useEffect(() => {
    const resetKey = props.resetAirportFlowKey;
    if (!resetKey || resetKey === lastResetAirportFlowKeyRef.current) {
      return;
    }

    lastResetAirportFlowKeyRef.current = resetKey;
    closeAirportBookingInMainView();
    setAirportListOpen(false);
    setAirportFilterOpen(false);
    setAirportRouteError(undefined);
    setAirportApiError(undefined);
  }, [props.resetAirportFlowKey]);

  const slideBackToMainMissionList = () => {
    const returnToListView = airportBookingReturnToListView;
    closeAirportBookingInMainView();
    props.onAirportBookingExitToOrigin?.(returnToListView);
  };

  const getAirportFlowStepForNativePage = (page: number): AirportFlowStep | undefined => {
    if (page <= 0) {
      return undefined;
    }

    if (page === getAirportNativePage('purpose')) {
      return 'purpose';
    }

    if (page === getAirportNativePage('list') && airportVisitType !== ApiAirportVisitType.Departing) {
      return 'list';
    }

    if (page === getAirportNativePage('route')) {
      return 'route';
    }

    if (page === getAirportNativePage('list')) {
      return 'list';
    }

    return undefined;
  };

  const getAirportPreviousNativePage = (page: number) => {
    switch (getAirportFlowStepForNativePage(page)) {
      case 'purpose':
        return 0;
      case 'route':
        return getAirportNativePage('purpose');
      case 'list':
        return airportVisitType === ApiAirportVisitType.Departing ? getAirportNativePage('route') : getAirportNativePage('purpose');
      default:
        return page;
    }
  };

  const getAirportNextNativePage = (page: number) => {
    switch (getAirportFlowStepForNativePage(page)) {
      case 'purpose':
        return airportVisitType === ApiAirportVisitType.Departing ? getAirportNativePage('route') : getAirportNativePage('list');
      case 'route':
        return getAirportNativePage('list');
      case 'list':
      default:
        return page;
    }
  };

  const getIosAdjustedAirportFlowPage = (page: number, x: number) => {
    if (Platform.OS !== 'ios' || !contentWidth) {
      return page;
    }

    const fallbackStartPage = getAirportNativePage(airportFlowStep);
    const startPage = airportFlowDragStartPageRef.current ?? fallbackStartPage;
    const startX = airportFlowDragStartXRef.current || contentWidth * startPage;
    const deltaX = x - startX;
    const dragThreshold = Math.max(24, contentWidth * 0.08);

    if (deltaX <= -dragThreshold) {
      return getAirportPreviousNativePage(startPage);
    }

    if (deltaX >= dragThreshold) {
      return getAirportNextNativePage(startPage);
    }

    return startPage;
  };

  const openAirportPurposeStep = () => {
    setAirportApiError(undefined);
    setAirportFlowStep('purpose');

    scrollAirportFlowToNativePage(getAirportNativePage('purpose'));
  };

  const openAirportRouteStep = () => {
    setAirportApiError(undefined);
    setAirportFlowStep('route');

    if ((airportZones?.length ?? 0) <= 0 && !airportZonesLoading) {
      void loadAirportZones({ force: true });
    }

    scrollAirportFlowToNativePage(getAirportNativePage('route'));
  };

  const openAirportListStep = () => {
    setAirportFlowStep('list');

    scrollAirportFlowToNativePage(getAirportNativePage('list'));
  };

  const airportRouteSwipeResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => false,
      onMoveShouldSetPanResponder: (_, gestureState) => Math.abs(gestureState.dx) > 16 && Math.abs(gestureState.dx) > Math.abs(gestureState.dy),
      onPanResponderRelease: (_, gestureState) => {
        if (gestureState.dx > 60) {
          openAirportPurposeStep();
        }
      },
      onPanResponderTerminate: () => undefined,
    })
  ).current;

  const isAirportDepartureDateBeforeToday = (value?: string) => {
    const parsedDate = moment(value, airportDateFormats, true);
    if (!parsedDate.isValid()) {
      return false;
    }

    return parsedDate.isBefore(airportDepartureMinDate, 'day');
  };

  const isAirportDepartureDateAfterMax = (value?: string) => {
    const parsedDate = moment(value, airportDateFormats, true);
    if (!parsedDate.isValid()) {
      return false;
    }

    return parsedDate.isAfter(airportDepartureMaxDate, 'day');
  };

  const isAirportDepartureDateValid = (value?: string) => {
    const parsedDate = moment(value, airportDateFormats, true);
    return parsedDate.isValid();
  };

  const isAirportDepartureTimeValid = (value?: string) => {
    const parsedTime = moment(value, ['h:mm A', 'HH:mm'], true);
    return parsedTime.isValid();
  };

  const formatAirportSearchDateTime = (value: Date) => {
    return moment(value).format('YYYY-MM-DDTHH:mm:ssZ');
  };

  const airportSearchDateTime = useMemo(() => {
    const parsedDate = moment(airportDepartureDate, airportDateFormats, true);
    const parsedTime = moment(airportDepartureTime, ['h:mm A', 'HH:mm'], true);

    if (!parsedDate.isValid() || !parsedTime.isValid()) {
      return undefined;
    }

    const localDateTime = parsedDate.clone().hour(parsedTime.hour()).minute(parsedTime.minute()).second(0).millisecond(0).toDate();

    // The generated client serializes this field through Date.toISOString().
    // Shadow it so the backend receives the wall-clock time the user entered.
    Object.defineProperty(localDateTime, 'toISOString', {
      value: () => formatAirportSearchDateTime(localDateTime),
      configurable: true,
    });

    return localDateTime;
  }, [airportDepartureDate, airportDepartureTime]);
  const airportPurposeSearchDateTime = useMemo(() => {
    const parsedDate = moment(airportPurposeDate, airportDateFormats, true);
    const parsedTime = moment(airportPurposeTime, ['h:mm A', 'HH:mm'], true);

    if (!parsedDate.isValid() || !parsedTime.isValid()) {
      return undefined;
    }

    const localDateTime = parsedDate.clone().hour(parsedTime.hour()).minute(parsedTime.minute()).second(0).millisecond(0).toDate();

    Object.defineProperty(localDateTime, 'toISOString', {
      value: () => formatAirportSearchDateTime(localDateTime),
      configurable: true,
    });

    return localDateTime;
  }, [airportPurposeDate, airportPurposeTime]);
  const airportMissionSearchDateTime = useMemo(() => {
    if (airportVisitType === ApiAirportVisitType.Departing) {
      return airportSearchDateTime;
    }

    if (airportVisitTypeUsesPurposeDateTime(airportVisitType)) {
      return airportPurposeSearchDateTime;
    }

    return undefined;
  }, [airportPurposeSearchDateTime, airportSearchDateTime, airportVisitType]);
  const airportMissionSearchDateTimeText = useMemo(
    () => (airportMissionSearchDateTime ? formatAirportSearchDateTime(airportMissionSearchDateTime) : undefined),
    [airportMissionSearchDateTime],
  );

  useEffect(() => {
    props.onAirportStartDateChange?.(airportBookingBranchId ? airportMissionSearchDateTime : undefined);
  }, [props.onAirportStartDateChange, airportBookingBranchId, airportMissionSearchDateTime]);

  const getAirportPurposeValidationError = () => {
    if (airportVisitType === undefined) {
      return t('airport-purpose-error-required');
    }

    if (!airportVisitTypeUsesPurposeDateTime(airportVisitType)) {
      return undefined;
    }

    const usesArrivalCopy = airportVisitType === ApiAirportVisitType.Arriving;
    const dateLabel = getAirportPurposeDateLabel();
    const timeLabel = getAirportPurposeTimeLabel();

    if (!airportPurposeDate) {
      return usesArrivalCopy ? t('airport-purpose-error-arrival-date-required') : formatAirportPurposeFieldError(dateLabel, t('field_required'));
    }

    if (!isAirportDepartureDateValid(airportPurposeDate)) {
      return usesArrivalCopy ? t('airport-purpose-error-arrival-date-invalid') : formatAirportPurposeFieldError(dateLabel, t('field_value_not_valid'));
    }

    if (isAirportDepartureDateBeforeToday(airportPurposeDate)) {
      return usesArrivalCopy ? t('airport-purpose-error-arrival-date-past') : formatAirportPurposeFieldError(dateLabel, t('field_value_not_valid'));
    }

    if (isAirportDepartureDateAfterMax(airportPurposeDate)) {
      return usesArrivalCopy ? t('airport-purpose-error-arrival-date-invalid') : formatAirportPurposeFieldError(dateLabel, t('field_value_not_valid'));
    }

    if (!airportPurposeTime) {
      return usesArrivalCopy ? t('airport-purpose-error-arrival-time-required') : formatAirportPurposeFieldError(timeLabel, t('field_required'));
    }

    if (!isAirportDepartureTimeValid(airportPurposeTime)) {
      return usesArrivalCopy ? t('airport-purpose-error-arrival-time-invalid') : formatAirportPurposeFieldError(timeLabel, t('field_value_not_valid'));
    }

    return undefined;
  };

  const getAirportRouteValidationError = () => {
    if (!airportDepartureDate || !airportDepartureTime || !airportDestination || !airportConnectionDestination) {
      return t('airport-route-error-required');
    }

    if (!isAirportDepartureDateValid(airportDepartureDate)) {
      return t('airport-route-error-invalid-date');
    }

    if (isAirportDepartureDateBeforeToday(airportDepartureDate)) {
      return t('airport-route-error-past-date');
    }

    if (isAirportDepartureDateAfterMax(airportDepartureDate)) {
      return t('airport-route-error-invalid-date');
    }

    if (!isAirportDepartureTimeValid(airportDepartureTime)) {
      return t('airport-route-error-invalid-time');
    }

    return undefined;
  };

  const isAirportRouteFormValid = !getAirportRouteValidationError();

  const handleAirportPurposeContinue = async () => {
    const validationError = getAirportPurposeValidationError();
    if (validationError) {
      setAirportPurposeError(validationError);
      return;
    }

    setAirportPurposeError(undefined);
    setAirportApiError(undefined);

    if (airportVisitType === ApiAirportVisitType.Departing) {
      openAirportRouteStep();
      return;
    }

    setAirportSelectedTagIds([]);
    await fetchAirportMissions({ selectedTagIds: [], openListOnSuccess: true });
  };

  const openAirportStepBeforeList = () => {
    if (airportVisitType === ApiAirportVisitType.Departing) {
      openAirportRouteStep();
      return;
    }

    openAirportPurposeStep();
  };

  const mainFilterTypeNames = useMemo(() => {
    const allMainCards = [...nonAirportPinnedMissionCards, ...(props.missionCards ?? []), ...(props.flashMissionCards ?? [])];
    return Array.from(new Set(allMainCards.map((card) => getAirportBusinessType(card)))).sort((left, right) => left.localeCompare(right));
  }, [nonAirportPinnedMissionCards, props.missionCards, props.flashMissionCards]);

  const mainFilterTypeSignature = useMemo(() => mainFilterTypeNames.join('|'), [mainFilterTypeNames]);

  useEffect(() => {
    if (props.syncWithMapFilters) {
      return;
    }

    setMainFilterItems((prev) => {
      const next = mainFilterTypeNames.map((typeName) => {
        const previous = prev.find((x) => x.id === typeName);
        return {
          id: typeName,
          name: typeName,
          checked: previous?.checked ?? false,
        };
      });

      const unchanged =
        prev.length === next.length &&
        prev.every((item, index) => {
          const candidate = next[index];
          return candidate && item.id === candidate.id && item.name === candidate.name && item.checked === candidate.checked;
        });

      return unchanged ? prev : next;
    });
  }, [props.syncWithMapFilters, mainFilterTypeSignature]);

  const filteredAirportMissionCardsForDisplay = useMemo(() => sortPublicAirportZoneLast(airportApiMissionCards), [airportApiMissionCards]);

  const mainFilterSelectedTypes = useMemo(() => mainFilterItems.filter((x) => x.checked).map((x) => x.id), [mainFilterItems]);

  const cardPassesMainFilters = (card: MissionCardProps) => {
    if (props.syncWithMapFilters) return true;
    const byType = mainFilterSelectedTypes.length <= 0 || mainFilterSelectedTypes.includes(getAirportBusinessType(card));
    const byAvailability = !mainOnlyAcceptableMissions || isMissionAvailableForDiscovery(card);
    const byUpcoming = mainShowUpcomingMissions || !card.upcomingPeriod;

    return byType && byAvailability && byUpcoming;
  };

  const filteredPinnedMissionCardsForDisplay = useMemo(
    () => (props.syncWithMapFilters ? nonAirportPinnedMissionCards : nonAirportPinnedMissionCards.filter((card) => cardPassesMainFilters(card))),
    [nonAirportPinnedMissionCards, mainFilterSelectedTypes, mainOnlyAcceptableMissions, mainShowUpcomingMissions, props.syncWithMapFilters],
  );

  const filteredMissionCardsForDisplay = useMemo(
    () => (props.syncWithMapFilters ? (props.missionCards ?? []) : (props.missionCards ?? []).filter((card) => cardPassesMainFilters(card))),
    [props.missionCards, props.syncWithMapFilters, mainFilterSelectedTypes, mainOnlyAcceptableMissions, mainShowUpcomingMissions],
  );

  const filteredFlashMissionCardsForDisplay = useMemo(
    () => (props.syncWithMapFilters ? (props.flashMissionCards ?? []) : (props.flashMissionCards ?? []).filter((card) => cardPassesMainFilters(card))),
    [props.flashMissionCards, props.syncWithMapFilters, mainFilterSelectedTypes, mainOnlyAcceptableMissions, mainShowUpcomingMissions],
  );

  const handleAirportBannerPress = () => {
    const primaryAirportMissionCard = airportMissionCards[0];
    if (!primaryAirportMissionCard?.id) {
      return;
    }

    openAirportBookingInMainView(primaryAirportMissionCard.id);
  };

  const handleAgeRestrictedBannerPress = () => {
    setAirportListOpen(false);
    setMissionFilter(FilterType.AgeRestricted);
  };

  const handleAirportAvailabilityPress = (branchId?: string) => {
    if (!branchId) {
      return;
    }

    openAirportBookingInMainView(branchId);
  };

  const shouldGroupAirportMissions = airportMissionCards.length > 1;
  const showAirportOnlyView = missionFilter === FilterType.All && shouldGroupAirportMissions && airportListOpen;
  const showNoMissionMessage =
    (showAirportOnlyView && airportMissionCards.length <= 0) ||
    (!showAirportOnlyView &&
      missionFilter === FilterType.All &&
      !hasAirportMissions &&
      filteredMissionCardsForDisplay.length <= 0 &&
      filteredPinnedMissionCardsForDisplay.length <= 0 &&
      filteredFlashMissionCardsForDisplay.length <= 0) ||
    (!showAirportOnlyView && missionFilter === FilterType.Classic && filteredMissionCardsForDisplay.length <= 0
      && !filteredPinnedMissionCardsForDisplay.some(card => matchesMissionTypeFilter(card, FilterType.Classic))) ||
    (!showAirportOnlyView && missionFilter === FilterType.Flash && filteredFlashMissionCardsForDisplay.length <= 0
      && !filteredPinnedMissionCardsForDisplay.some(card => matchesMissionTypeFilter(card, FilterType.Flash))) ||
    (!showAirportOnlyView && missionFilter === FilterType.Featured && filteredPinnedMissionCardsForDisplay.length <= 0) ||
    (!showAirportOnlyView && missionFilter === FilterType.AgeRestricted &&
      ![...filteredPinnedMissionCardsForDisplay, ...filteredMissionCardsForDisplay, ...filteredFlashMissionCardsForDisplay]
        .some(card => matchesMissionTypeFilter(card, FilterType.AgeRestricted)));

  const missionListHeaderComponent = useMemo(() => {
    if (props.loadingSpin) {
      return null;
    }

    if (showAirportOnlyView) {
      return (
        <>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Label style={styles.subSession}>Airport</Label>
            <Pressable
              onPress={() => setAirportListOpen(false)}
              style={{
                marginRight: 12,
                paddingHorizontal: 8,
                paddingVertical: 4,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <FontAwesomeIcon icon={faChevronLeft} color={airportListAccentColor} />
              <Label variant={LabelType.caption} style={{ color: airportListAccentColor }}>
                {t('back')}
              </Label>
            </Pressable>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginHorizontal: 12, marginBottom: 8 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <FontAwesomeIcon icon={faPlaneDeparture} color={airportListAccentColor} />
              <Label variant={LabelType.titleDark} style={{ color: airportListTitleColor }}>
                {t('Airport')}
              </Label>
            </View>
            <Label variant={LabelType.caption} style={{ color: airportListTextColor }}>
              {airportMissionCards.length}
            </Label>
          </View>
        </>
      );
    }

    return (
      <>
        {airportMissionCards.length > 0 && (
          <View style={{ marginHorizontal: 12, marginBottom: 8 }}>
            <Pressable
              onPress={handleAirportBannerPress}
              style={{
                borderRadius: 8,
                backgroundColor: '#c9e8fb',
                minHeight: 58,
                paddingHorizontal: 10,
                paddingVertical: 8,
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  flex: 1,
                }}
              >
                <Image source={airportBannerListIcon} style={{ width: 36, height: 32, marginRight: 10 }} resizeMode="contain" />
                <View style={{ flex: 1 }}>
                  <Label style={{ color: '#0a4f84', fontWeight: '700', fontSize: 19, lineHeight: 21 }}>{t('airport-trip-popup-title')}</Label>
                  <Label style={{ color: '#0a67a6', fontWeight: '700', fontSize: 14, marginTop: 2, lineHeight: 16 }}>{t('airport-trip-popup-subtitle')}</Label>
                </View>
                <FontAwesomeIcon icon={faChevronRight} size={16} color={'#1c6eb5'} />
              </View>
            </Pressable>
          </View>
        )}
        {airportMissionCards.length <= 0 && props.showAgeRestrictedPromotion && missionFilter !== FilterType.AgeRestricted && (
          <MissionPromotionBanner
            title={t('age-restricted-promotion-title')}
            subtitle={t('age-restricted-promotion-subtitle')}
            icon={<AgeRestrictedMissionIcon size={34} color={AGE_RESTRICTED_DARK_COLOR} />}
            backgroundColor={AGE_RESTRICTED_BANNER_COLOR}
            titleColor={AGE_RESTRICTED_DARK_COLOR}
            subtitleColor={AGE_RESTRICTED_PRIMARY_COLOR}
            chevronColor={AGE_RESTRICTED_PRIMARY_COLOR}
            onPress={handleAgeRestrictedBannerPress}
          />
        )}
        {isMobileMissionList && (
          <View
            style={{
              marginHorizontal: 12,
              marginBottom: 8,
              position: 'relative',
              zIndex: mainFilterOpen ? 60 : 2,
              elevation: mainFilterOpen ? 60 : 2,
            }}
          >
            <CompactMissionFilters
              expanded={props.compactFiltersExpanded}
              onExpandedChange={props.onCompactFiltersExpandedChange}
              items={props.syncWithMapFilters && (props.mapFilterItems?.length ?? 0) > 0 ? props.mapFilterItems! : mainFilterItems}
              selectedFilterType={missionFilter}
              showMissionTypeFilters
              onlyAcceptableMissions={
                props.syncWithMapFilters && props.mapOnlyAcceptableMissions !== undefined ? props.mapOnlyAcceptableMissions : mainOnlyAcceptableMissions
              }
              showUpcomingMissions={
                props.syncWithMapFilters && props.mapShowUpcomingMissions !== undefined ? props.mapShowUpcomingMissions : mainShowUpcomingMissions
              }
              open={mainFilterOpen}
              onOpenChange={setMainFilterOpen}
              onFilterTypeChange={(next) => {
                setMissionFilter(next);
              }}
              onItemsChange={(selectedItems) => {
                if (props.syncWithMapFilters && (props.mapFilterItems?.length ?? 0) > 0 && props.onMapFilterSelectionChange) {
                  props.onMapFilterSelectionChange(selectedItems);
                } else {
                  setMainFilterItems((prev) =>
                    prev.map((item) => ({
                      ...item,
                      checked: selectedItems.includes(item.id),
                    }))
                  );
                }
              }}
              onOnlyAcceptableMissionsChange={(next) => {
                if (props.syncWithMapFilters && props.onMapOnlyAcceptableMissionsChange) {
                  props.onMapOnlyAcceptableMissionsChange(!next);
                } else {
                  setMainOnlyAcceptableMissions(next);
                }
              }}
              onShowUpcomingMissionsChange={(next) => {
                if (props.syncWithMapFilters && props.onMapShowUpcomingMissionsChange) {
                  props.onMapShowUpcomingMissionsChange(next);
                } else {
                  setMainShowUpcomingMissions(next);
                }
              }}
            />
          </View>
        )}
      </>
    );
  }, [
    airportMissionCards.length,
    handleAirportBannerPress,
    isMobileMissionList,
    mainFilterItems,
    mainFilterOpen,
    mainOnlyAcceptableMissions,
    mainShowUpcomingMissions,
    missionFilter,
    props.loadingSpin,
    props.compactFiltersExpanded,
    props.onCompactFiltersExpandedChange,
    props.mapFilterItems,
    props.mapOnlyAcceptableMissions,
    props.mapShowUpcomingMissions,
    props.onMapFilterSelectionChange,
    props.onMapOnlyAcceptableMissionsChange,
    props.onMapShowUpcomingMissionsChange,
    props.showAgeRestrictedPromotion,
    props.syncWithMapFilters,
    showAirportOnlyView,
    t,
    theme.text?.darkTurquoiseTwo,
  ]);

  const missionListRows = useMemo<MissionListRow[]>(() => {
    if (props.loadingSpin) {
      return [];
    }

    const rows: MissionListRow[] = [];

    if (showAirportOnlyView) {
      airportMissionCards.forEach((cardProps) => {
        rows.push({ type: 'missionCard', id: `airport-only-${cardProps.id}`, card: cardProps });
      });
      if (rows.length === 0) {
        rows.push({ type: 'empty', id: 'empty-airport-only' });
      }
      return rows;
    }

    const visibleCards = getDistanceSortedMissionCards(
      filteredPinnedMissionCardsForDisplay,
      filteredMissionCardsForDisplay,
      filteredFlashMissionCardsForDisplay,
      missionFilter,
    );
    if (visibleCards.length > 0 || (missionFilter === FilterType.All && airportMissionCards.length > 0)) {
      rows.push({ type: 'sectionHeader', id: 'section-missions', title: t('Missions') });
      if (missionFilter === FilterType.All) {
        airportMissionCards.forEach((airportCard) => {
          rows.push({ type: 'airportEntry', id: `airport-entry-${airportCard.id}`, card: airportCard });
        });
      }
      visibleCards.forEach((cardProps) => {
        rows.push({ type: 'missionCard', id: `mission-${getMissionListingKey(cardProps)}`, card: cardProps, pinned: cardProps.pinned });
      });
    }

    if (showNoMissionMessage) {
      rows.push({ type: 'empty', id: 'empty-main-list' });
    }

    return rows;
  }, [
    airportMissionCards,
    filteredFlashMissionCardsForDisplay,
    filteredMissionCardsForDisplay,
    filteredPinnedMissionCardsForDisplay,
    missionFilter,
    props.loadingSpin,
    showAirportOnlyView,
    showNoMissionMessage,
    t,
  ]);

  const renderMissionListRow = ({ item }: { item: MissionListRow }) => {
    switch (item.type) {
      case 'sectionHeader':
        return <Label style={styles.subSession}>{item.title}</Label>;
      case 'missionCard':
        return (
          <MissionCard
            {...item.card}
            drawerVersion={props.drawerVersion}
            showAsCards={props.showAsCards}
            selectedMission={selected}
            loading={loading?.id === item.card?.id}
            pinned={item.pinned}
          />
        );
      case 'airportEntry':
        return (
          <Pressable onPress={() => handleAirportAvailabilityPress(item.card.id)} style={styles.list.airportEntry.container}>
            <View style={styles.list.airportEntry.iconBase}>
              <Image source={airportLocationLogo} style={styles.list.airportEntry.iconImage as any} resizeMode="cover" />
            </View>
            <View style={styles.list.airportEntry.content}>
              <Label variant={LabelType.title} style={styles.list.airportEntry.title}>
                {item.card.title ?? t('Airport')}
              </Label>
              <Label variant={LabelType.caption} style={styles.list.airportEntry.subtitle}>
                {t('airport-trip-popup-subtitle')}
              </Label>
            </View>
            <View style={styles.list.airportEntry.meta}>
              <View style={styles.list.airportEntry.countBadge}>
                <Label style={styles.list.airportEntry.countText}>{item.card.airportMissionCount ?? 0}</Label>
              </View>
              <FontAwesomeIcon icon={faChevronRight} size={14} style={styles.list.airportEntry.chevron as any} />
            </View>
          </Pressable>
        );
      case 'empty':
        return (
          <View style={{ flexGrow: 1, padding: 16, paddingTop: 16, alignItems: 'center', justifyContent: 'center', gap: 24 }}>
            <FontAwesomeIcon icon={faFilterSlash} size={50} color={airportListAccentColor} />
            <Label numberOfLines={5} style={{ color: airportListAccentColor, fontSize: 14 }}>
              {t('no-mission-filter')}
            </Label>
          </View>
        );
      default:
        return null;
    }
  };

  const missionListContentContainerStyle = useMemo(
    () => ({
      flexGrow: missionListRows.length === 1 && missionListRows[0]?.type === 'empty' ? 1 : 0,
      paddingBottom: 16,
    }),
    [missionListRows]
  );

  const hasPendingAirportAutoOpen = !!props.autoOpenAirportBookingKey && props.autoOpenAirportBookingKey !== lastAutoOpenAirportKeyRef.current;
  const showAirportBookingFlow = hasAirportBookingFlow && (isWeb || contentWidth > 0);
  const isAirportFlowTransitioning = hasPendingAirportAutoOpen || (!!airportBookingBranchId && !showAirportBookingFlow);
  const initialAirportFlowPage = !isWeb && contentWidth > 0 ? getAirportNativePage(airportFlowStep) : 0;
  const airportRoutePanHandlers = !isWeb ? airportRouteSwipeResponder.panHandlers : {};
  const nativeAirportFlowScrollEnabled = airportFlowStep === 'list' || (airportFlowStep === 'route' && isAirportRouteFormValid);
  const useIosAirportPagingFix = Platform.OS === 'ios';
  const shouldRenderAirportRouteNativePage = !useIosAirportPagingFix || airportVisitType === ApiAirportVisitType.Departing;
  const airportFlowPagerKey = useIosAirportPagingFix ? `airport-flow-ios-${airportVisitType === ApiAirportVisitType.Departing ? 'departing' : 'compact'}` : 'airport-flow-default';
  const airportRouteBackgroundColor = airportRouteBackgroundColorFixed;
  const airportRouteHeadingColor = airportListAccentColorFixed;
  const airportRouteTextColor = airportListTextColorFixed;
  const airportRouteFieldStyle = {
    backgroundColor: airportRouteInputFieldStyle.backgroundColor,
    borderColor: airportRouteInputFieldStyle.borderColor,
  };
  const airportRouteValueTextColor = airportRouteInputValueTextColor;
  const airportRoutePlaceholderColor = airportRouteInputPlaceholderTextColor;
  const airportRouteIconColor = airportRouteInputIconColor;
  const airportRouteRadioBorderColor = '#56aef0';
  const airportRouteRadioFillColor = airportListAccentColorFixed;
  const airportRoutePrimaryButtonColor = '#1ea0ef';
  const airportRouteErrorColor = airportListErrorColorFixed;
  const airportRouteScrollBottomPadding = isNativeMobile ? 120 : 32;

  useEffect(() => {
    if (!showAirportBookingFlow || airportFlowStep !== 'route') {
      setAirportRouteScrollY(0);
      return;
    }

    airportRouteScrollRef.current?.scrollTo({ y: 0, animated: false });
    setAirportRouteScrollY(0);
  }, [showAirportBookingFlow, airportFlowStep]);

  useEffect(() => {
    if (!useIosAirportPagingFix) {
      return;
    }

    resetAirportFlowDragTracking();
    airportFlowProgrammaticTargetPageRef.current = undefined;
  }, [useIosAirportPagingFix, useCompactIosAirportPages]);

  const airportRouteActualOverflow = Math.max(0, airportRouteContentHeight - airportRouteViewportHeight - airportRouteScrollBottomPadding);
  const showAirportRouteScrollHint = airportRouteActualOverflow > 8 && airportRouteScrollY < airportRouteActualOverflow - 8;
  const handleAirportRouteScrollHintPress = () => {
    if (!showAirportRouteScrollHint) {
      return;
    }

    const scrollStep = Math.max(airportRouteViewportHeight * 0.7, 180);
    const nextScrollY = Math.min(airportRouteScrollY + scrollStep, airportRouteActualOverflow);

    airportRouteScrollRef.current?.scrollTo({ y: nextScrollY, animated: true });
  };
  const defaultThemePrimaryTextColor = theme.text?.primary?.toString() ?? '#000000';
  const airportListBackgroundColor = isAirportDarkMode ? (theme.background?.primary?.toString() ?? '#2b3a46') : airportListBackgroundColorFixed;
  const airportFlowBackgroundColor = airportFlowStep === 'list' ? airportListBackgroundColor : airportRouteBackgroundColor;
  const airportListAccentColor = isAirportDarkMode ? (theme.text?.darkTurquoiseTwo?.toString() ?? '#5FC8BA') : airportListAccentColorFixed;
  const airportListTitleColor = isAirportDarkMode ? (theme.text?.primary?.toString() ?? '#ffffff') : airportListTitleColorFixed;
  const airportListTextColor = isAirportDarkMode ? (theme.text?.primary?.toString() ?? '#ffffff') : airportListTextColorFixed;
  const airportListCardBackgroundColor = isAirportDarkMode ? (theme.background?.missionDetail?.toString() ?? '#465866') : airportListCardBackgroundColorFixed;
  const airportListCardInnerBackgroundColor = isAirportDarkMode
    ? (theme.background?.primaryLight?.toString() ?? '#344450')
    : airportListCardInnerBackgroundColorFixed;
  const airportListBorderColor = isAirportDarkMode ? (theme.borderColorLight?.toString() ?? '#5F7486') : airportListBorderColorFixed;
  const airportListLogoBackgroundColor = isAirportDarkMode ? (theme.background?.primaryLight?.toString() ?? '#344450') : airportListLogoBackgroundColorFixed;
  const airportListErrorColor = isAirportDarkMode ? (theme.text?.error?.toString() ?? '#FF9BB4') : airportListErrorColorFixed;
  const airportListImageIconColor = isAirportDarkMode ? '#ffffff' : airportListAccentColor;
  const airportTripBannerBackgroundColor = airportListCardBackgroundColor;
  const airportTripBannerBorderColor = airportListBorderColor;
  const airportTripBannerTextColor = airportListTextColor;
  const airportTripBannerHeaderBackgroundColor = airportTripBannerBackgroundColor;
  const airportTripBannerTileBackgroundColor = airportTripBannerBackgroundColor;
  const airportTripPlaneIcon = airplaneFlyingIcon;
  const airportTripEditIcon = editIcon;
  const airportGateIcon = gateIcon;
  const pullToRefreshEnabled =
    isNativeMobile &&
    isMobileMissionList &&
    ((showAirportBookingFlow && airportFlowStep === 'list') || (!showAirportBookingFlow && !!props.onMissionListRefresh));

  const handleMissionListRefresh = async () => {
    if (!pullToRefreshEnabled || isPullRefreshing) {
      return;
    }

    setIsPullRefreshing(true);
    try {
      if (showAirportBookingFlow && airportFlowStep === 'list') {
        await fetchAirportMissions();
      } else if (!showAirportBookingFlow) {
        await props.onMissionListRefresh?.();
      }
    } finally {
      setIsPullRefreshing(false);
    }
  };

  const missionListRefreshControl = pullToRefreshEnabled ? (
    <RefreshControl
      refreshing={isPullRefreshing}
      onRefresh={() => {
        void handleMissionListRefresh();
      }}
      tintColor={showAirportBookingFlow && airportFlowStep === 'list' ? airportListAccentColor : defaultThemePrimaryTextColor}
      colors={[showAirportBookingFlow && airportFlowStep === 'list' ? airportListAccentColor : defaultThemePrimaryTextColor]}
    />
  ) : undefined;

  const mainMissionList = props.loadingSpin ? (
    <View style={{ flexGrow: 1, padding: 16, paddingTop: 16, alignItems: 'center', justifyContent: 'center' }}>
      <Loading />
    </View>
  ) : (
    <FlatList
      ref={mainMissionListRef}
      data={missionListRows}
      keyExtractor={(item) => item.id}
      renderItem={renderMissionListRow}
      ListHeaderComponent={missionListHeaderComponent}
      contentContainerStyle={missionListContentContainerStyle}
      refreshControl={missionListRefreshControl}
      keyboardShouldPersistTaps="handled"
      nestedScrollEnabled={true}
      initialNumToRender={12}
      maxToRenderPerBatch={12}
      windowSize={8}
      updateCellsBatchingPeriod={16}
      removeClippedSubviews={Platform.OS !== 'web'}
    />
  );

  return (
    <View style={[styles.container, { flex: 1, flexDirection: props.showAsCards ? 'row' : 'column' }]}>
      {!hasAirportBookingFlow && !isAirportFlowTransitioning && (
        <View style={styles.title}>
          <Label variant={LabelType.titleLargeNoCapsPrimary} style={{ ...StylesHelper.padding(16), marginTop: -4 }}>
            {t('Missions')}
          </Label>
          <MoreInfoButton title={t('help_support')} openURL={'missions-lanla-website-info-link'}>{`${t(
            'browse-through-the-available-missions'
          )}`}</MoreInfoButton>
        </View>
      )}
      <View
        style={{ flex: 1 }}
        onLayout={(event) => {
          const width = event.nativeEvent.layout.width;
          if (width > 0 && Math.abs(width - contentWidth) > 1) {
            setContentWidth(width);
          }
        }}
      >
        {isAirportFlowTransitioning ? (
          <View style={{ flex: 1, backgroundColor: airportRouteBackgroundColor, alignItems: 'center', justifyContent: 'center' }}>
            <Loading />
          </View>
        ) : showAirportBookingFlow ? (
          <ScrollView
            key={airportFlowPagerKey}
            ref={pagingRef}
            style={isWeb ? { flex: 1, backgroundColor: airportFlowBackgroundColor } : undefined}
            contentContainerStyle={isWeb ? { flexGrow: 1, backgroundColor: airportFlowBackgroundColor } : undefined}
            horizontal={!isWeb}
            pagingEnabled={!isWeb}
            scrollEnabled={isWeb ? true : nativeAirportFlowScrollEnabled}
            disableIntervalMomentum={useIosAirportPagingFix}
            decelerationRate={useIosAirportPagingFix ? 'fast' : undefined}
            bounces={useIosAirportPagingFix ? false : undefined}
            alwaysBounceHorizontal={useIosAirportPagingFix ? false : undefined}
            showsHorizontalScrollIndicator={isWeb ? undefined : false}
            showsVerticalScrollIndicator={isWeb}
            contentOffset={{ x: contentWidth * initialAirportFlowPage, y: 0 }}
            onScrollBeginDrag={(event: NativeSyntheticEvent<any>) => {
              const dragStartX = event.nativeEvent.contentOffset.x ?? contentWidth * getAirportNativePage(airportFlowStep);
              airportFlowProgrammaticTargetPageRef.current = undefined;
              airportFlowUserScrollRef.current = true;
              airportFlowDragStartXRef.current = dragStartX;
              airportFlowDragStartPageRef.current = contentWidth > 0 ? Math.round(dragStartX / contentWidth) : getAirportNativePage(airportFlowStep);
            }}
            onMomentumScrollEnd={(event: NativeSyntheticEvent<any>) => {
              if (isWeb || !contentWidth) {
                return;
              }

              if (!useIosAirportPagingFix && !airportFlowUserScrollRef.current) {
                return;
              }

              const x = event.nativeEvent.contentOffset.x ?? 0;
              const page = Math.round(x / contentWidth);
              const programmaticTargetPage = airportFlowProgrammaticTargetPageRef.current;

              if (programmaticTargetPage !== undefined) {
                airportFlowProgrammaticTargetPageRef.current = undefined;
                resetAirportFlowDragTracking();

                if (page !== programmaticTargetPage) {
                  scrollAirportFlowToNativePage(programmaticTargetPage, false);
                }

                return;
              }

              airportFlowUserScrollRef.current = false;

              const effectivePage = getIosAdjustedAirportFlowPage(page, x);
              const effectiveStep = getAirportFlowStepForNativePage(effectivePage);
              airportFlowDragStartPageRef.current = undefined;
              airportFlowDragStartXRef.current = 0;

              if (effectivePage !== page) {
                scrollAirportFlowToNativePage(effectivePage, false);
              }

              if (!effectiveStep) {
                slideBackToMainMissionList();
                return;
              }

              if (effectiveStep === 'purpose') {
                setAirportFlowStep('purpose');
                return;
              }

              if (effectiveStep === 'route') {
                setAirportFlowStep('route');
                return;
              }

              if (effectiveStep === 'list' && airportFlowStep === 'purpose') {
                const validationError = getAirportPurposeValidationError();
                if (validationError) {
                  setAirportPurposeError(validationError);

                  scrollAirportFlowToNativePage(getAirportNativePage('purpose'));
                  return;
                }

                setAirportPurposeError(undefined);
                setAirportApiError(undefined);

                if (airportVisitType === ApiAirportVisitType.Departing) {
                  openAirportRouteStep();
                  return;
                }

                setAirportSelectedTagIds([]);
                void (async () => {
                  const hasLoadedMissions = await fetchAirportMissions({ selectedTagIds: [], openListOnSuccess: false });
                  if (!hasLoadedMissions) {
                    scrollAirportFlowToNativePage(getAirportNativePage('purpose'));
                    return;
                  }

                  setAirportFlowStep('list');
                })();

                return;
              }

              if (effectiveStep === 'list' && airportFlowStep === 'route') {
                const validationError = getAirportRouteValidationError();
                if (validationError) {
                  setAirportRouteError(validationError);

                  scrollAirportFlowToNativePage(getAirportNativePage('route'));
                  return;
                }

                setAirportRouteError(undefined);
                setAirportApiError(undefined);
                setAirportSelectedTagIds([]);
                void (async () => {
                  const hasLoadedMissions = await fetchAirportMissions({ selectedTagIds: [], openListOnSuccess: false });
                  if (!hasLoadedMissions) {
                    scrollAirportFlowToNativePage(getAirportNativePage('route'));
                    return;
                  }

                  setAirportFlowStep('list');
                })();

                return;
              }

              if (effectiveStep === 'list') {
                setAirportFlowStep('list');
              }
            }}
            keyboardShouldPersistTaps="handled"
          >
            {!isWeb && (
              <View style={{ width: contentWidth, flex: 1 }}>
                {!!hasAirportBookingFlow && <View style={{ height: mainMissionHeaderReservedHeight }} />}
                {mainMissionList}
              </View>
            )}
            <View style={{ width: isWeb ? '100%' : contentWidth, flex: 1, display: isWeb && airportFlowStep !== 'purpose' ? 'none' : 'flex' }}>
              <View style={{ flex: 1, backgroundColor: airportRouteBackgroundColor, position: 'relative' }}>
                <ScrollView
                  style={{ flex: 1, backgroundColor: airportRouteBackgroundColor }}
                  contentContainerStyle={{ flexGrow: 1 }}
                  keyboardShouldPersistTaps="handled"
                  nestedScrollEnabled={true}
                >
                  <View style={{ flex: 1, minHeight: '100%', backgroundColor: airportRouteBackgroundColor, overflow: 'hidden' }}>
                    <Image
                      source={airplaneTopMenu}
                      style={{ position: 'absolute', top: 0, right: -26, width: 126, height: 82, marginTop: 0, marginRight: 0 }}
                      resizeMode="contain"
                    />
                    <View style={{ paddingHorizontal: 20, paddingTop: 12, paddingBottom: airportRouteScrollBottomPadding, gap: 12 }}>
                      <Pressable
                        onPress={slideBackToMainMissionList}
                        style={{ flexDirection: 'row', alignItems: 'center', gap: 8, zIndex: 1, paddingVertical: 4, alignSelf: 'flex-start' }}
                      >
                        <View
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 10,
                            backgroundColor: airportRouteHeadingColor,
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} color={'#ffffff'} size={11} />
                        </View>
                        <Label style={{ color: airportRouteHeadingColor, fontSize: 16, fontWeight: '700' }}>{t('back')}</Label>
                      </Pressable>

                      <Label style={{ color: airportRouteHeadingColor, fontSize: 21, fontWeight: '700', marginTop: 2, zIndex: 1, lineHeight: 26 }}>
                        {t('airport-purpose-title')}
                      </Label>
                      <Label style={{ color: airportRouteTextColor, fontSize: 16, marginTop: -2, zIndex: 1, lineHeight: 22 }}>
                        {t('airport-purpose-description')}
                      </Label>

                      <View style={{ gap: 12, marginTop: 10 }}>
                        {airportVisitTypeOptions.map((option) => {
                          const selectedOption = airportVisitType === option.value;
                          return (
                            <Pressable
                              key={`airport-purpose-${option.value}`}
                              onPress={() => {
                                setAirportVisitType(option.value);
                                setAirportPurposeError(undefined);
                                setAirportApiError(undefined);
                              }}
                              style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}
                            >
                              <View
                                style={{
                                  width: 21,
                                  height: 21,
                                  borderRadius: 10.5,
                                  borderWidth: 1.5,
                                  borderColor: airportRouteRadioBorderColor,
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: airportRouteFieldStyle.backgroundColor,
                                }}
                              >
                                {selectedOption && <View style={{ width: 11, height: 11, borderRadius: 5.5, backgroundColor: airportRouteRadioFillColor }} />}
                              </View>
                              <Label style={{ color: airportRouteTextColor, fontSize: 16, fontWeight: selectedOption ? '700' : '400', flexShrink: 1 }}>
                                {option.label}
                              </Label>
                            </Pressable>
                          );
                        })}
                      </View>

                      {airportVisitTypeUsesPurposeDateTime(airportVisitType) && (
                        <>
                          <Label style={{ color: airportRouteHeadingColor, fontSize: 16, fontWeight: '600', marginTop: 10 }}>
                            {getAirportPurposeDateLabel()}
                          </Label>
                          <View style={{ flexDirection: 'row', gap: 8 }}>
                            <CustomDatePicker
                              containerStyle={{ flex: 1, flexBasis: 0, minWidth: 0 }}
                              fieldStyle={airportRouteFieldStyle}
                              valueTextColor={airportRouteValueTextColor}
                              placeholderTextColor={airportRoutePlaceholderColor}
                              iconColor={airportRouteIconColor}
                              selectedDate={airportPurposeDate}
                              placeholder={t('airport-route-date-placeholder')}
                              minDate={airportDepartureMinDate.toDate()}
                              maxDate={airportDepartureMaxDate.toDate()}
                              onOpen={() => {
                                setAirportPurposeError(undefined);
                                setAirportApiError(undefined);
                              }}
                              onChangeText={(value) => {
                                setAirportPurposeDate(value);
                                setAirportPurposeError(undefined);
                                setAirportApiError(undefined);
                              }}
                            />
                            <CustomTimePicker
                              containerStyle={{ flex: 1, flexBasis: 0, minWidth: 0 }}
                              fieldStyle={airportRouteFieldStyle}
                              valueTextColor={airportRouteValueTextColor}
                              placeholderTextColor={airportRoutePlaceholderColor}
                              iconColor={airportRouteIconColor}
                              selectedTime={airportPurposeTime}
                              placeholder={t('airport-route-time-placeholder')}
                              onOpen={() => {
                                setAirportPurposeError(undefined);
                                setAirportApiError(undefined);
                              }}
                              onChangeText={(value) => {
                                setAirportPurposeTime(value);
                                setAirportPurposeError(undefined);
                                setAirportApiError(undefined);
                              }}
                            />
                          </View>
                        </>
                      )}

                      {!!(airportPurposeError || airportApiError) && (
                        <Label style={{ color: airportRouteErrorColor }}>{airportPurposeError || airportApiError}</Label>
                      )}

                      <View style={{ alignItems: 'center', marginTop: 12 }}>
                        <CustomButton
                          title={t('continue')}
                          style={{ width: 136, minHeight: 46, backgroundColor: airportRoutePrimaryButtonColor, borderColor: airportRoutePrimaryButtonColor }}
                          textStyle={{ color: '#ffffff', fontSize: 16, fontWeight: '700' }}
                          disabled={airportMissionsLoading}
                          onPress={() => {
                            void handleAirportPurposeContinue();
                          }}
                        />
                      </View>
                    </View>
                  </View>
                </ScrollView>
                {airportMissionsLoading && (
                  <View
                    pointerEvents="none"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: `${airportRouteBackgroundColor}DD`,
                    }}
                  >
                    <Loading />
                  </View>
                )}
              </View>
            </View>
            {shouldRenderAirportRouteNativePage && (
              <View style={{ width: isWeb ? '100%' : contentWidth, flex: 1, display: isWeb && airportFlowStep !== 'route' ? 'none' : 'flex' }} {...airportRoutePanHandlers}>
                <View style={{ flex: 1, backgroundColor: airportRouteBackgroundColor, position: 'relative' }}>
                  <ScrollView
                    ref={airportRouteScrollRef}
                    style={{ flex: 1, backgroundColor: airportRouteBackgroundColor }}
                    contentContainerStyle={{ flexGrow: 1 }}
                    keyboardShouldPersistTaps="handled"
                    nestedScrollEnabled={true}
                    showsVerticalScrollIndicator={true}
                    persistentScrollbar={true}
                    indicatorStyle={isAirportDarkMode ? 'white' : 'black'}
                    onLayout={(event) => setAirportRouteViewportHeight(event.nativeEvent.layout.height)}
                    onContentSizeChange={(_, height) => setAirportRouteContentHeight(height)}
                    onScroll={(event) => setAirportRouteScrollY(event.nativeEvent.contentOffset.y)}
                    scrollEventThrottle={16}
                  >
                    <View style={{ flex: 1, minHeight: '100%', backgroundColor: airportRouteBackgroundColor, overflow: 'hidden' }}>
                      <Image
                        source={airplaneTopMenu}
                        style={{ position: 'absolute', top: 0, right: -26, width: 126, height: 82, marginTop: 0, marginRight: 0 }}
                        resizeMode="contain"
                      />
                      <View style={{ paddingHorizontal: 20, paddingTop: 12, paddingBottom: airportRouteScrollBottomPadding, gap: 10 }}>
                        <Pressable
                          onPress={openAirportPurposeStep}
                          style={{ flexDirection: 'row', alignItems: 'center', gap: 8, zIndex: 1, paddingVertical: 4, alignSelf: 'flex-start' }}
                        >
                          <View
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: 10,
                              backgroundColor: airportRouteHeadingColor,
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <FontAwesomeIcon icon={faChevronLeft} color={'#ffffff'} size={11} />
                          </View>
                          <Label style={{ color: airportRouteHeadingColor, fontSize: 16, fontWeight: '700' }}>{t('back')}</Label>
                        </Pressable>
                        <Label style={{ color: airportRouteHeadingColor, fontSize: 21, fontWeight: '700', marginTop: 2, zIndex: 1, lineHeight: 26 }}>
                          {t('airport-route-title')}
                        </Label>
                        <Label style={{ color: airportRouteTextColor, fontSize: 16, marginTop: -2, zIndex: 1, lineHeight: 22 }}>
                          {t('airport-route-description')}
                        </Label>

                        <Label style={{ color: airportRouteHeadingColor, fontSize: 16, fontWeight: '600', marginTop: 10 }}>
                          {t('airport-route-departure-date')}
                        </Label>
                        <View style={{ flexDirection: 'row', gap: 8 }}>
                          <CustomDatePicker
                            containerStyle={{ flex: 1, flexBasis: 0, minWidth: 0 }}
                            fieldStyle={airportRouteFieldStyle}
                            valueTextColor={airportRouteValueTextColor}
                            placeholderTextColor={airportRoutePlaceholderColor}
                            iconColor={airportRouteIconColor}
                            selectedDate={airportDepartureDate}
                            placeholder={t('airport-route-date-placeholder')}
                            minDate={airportDepartureMinDate.toDate()}
                            maxDate={airportDepartureMaxDate.toDate()}
                            onOpen={() => {
                              setAirportRouteError(undefined);
                              setAirportApiError(undefined);
                            }}
                            onChangeText={(value) => {
                              setAirportDepartureDate(value);
                              setAirportRouteError(undefined);
                              setAirportApiError(undefined);
                            }}
                          />
                          <CustomTimePicker
                            containerStyle={{ flex: 1, flexBasis: 0, minWidth: 0 }}
                            fieldStyle={airportRouteFieldStyle}
                            valueTextColor={airportRouteValueTextColor}
                            placeholderTextColor={airportRoutePlaceholderColor}
                            iconColor={airportRouteIconColor}
                            selectedTime={airportDepartureTime}
                            placeholder={t('airport-route-time-placeholder')}
                            onOpen={() => {
                              setAirportRouteError(undefined);
                              setAirportApiError(undefined);
                            }}
                            onChangeText={(value) => {
                              setAirportDepartureTime(value);
                              setAirportRouteError(undefined);
                              setAirportApiError(undefined);
                            }}
                          />
                        </View>

                        <Label style={{ color: airportRouteHeadingColor, fontSize: 16, fontWeight: '600', marginTop: 10 }}>
                          {t('airport-route-destination')}
                        </Label>
                        <View style={{ gap: 12 }}>
                          {airportZonesLoading && (
                            <View style={{ paddingVertical: 6 }}>
                              <Loading />
                            </View>
                          )}
                          {!airportZonesLoading &&
                            airportRouteOptions.map((zone) => {
                              const zoneId = zone.zoneId as string;
                              const selectedOption = airportDestination === zoneId;
                              return (
                                <Pressable
                                  key={`airport-destination-${zoneId}`}
                                  onPress={() => {
                                    setAirportDestination(zoneId);
                                    setAirportRouteError(undefined);
                                    setAirportApiError(undefined);
                                  }}
                                  style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}
                                >
                                  <View
                                    style={{
                                      width: 21,
                                      height: 21,
                                      borderRadius: 10.5,
                                      borderWidth: 1.5,
                                      borderColor: airportRouteRadioBorderColor,
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: airportRouteFieldStyle.backgroundColor,
                                    }}
                                  >
                                    {selectedOption && (
                                      <View style={{ width: 11, height: 11, borderRadius: 5.5, backgroundColor: airportRouteRadioFillColor }} />
                                    )}
                                  </View>
                                  <Label style={{ color: airportRouteTextColor, fontSize: 16, fontWeight: selectedOption ? '700' : '400' }}>
                                    {formatAirportRouteOptionLabel(zone.name)}
                                  </Label>
                                </Pressable>
                              );
                            })}
                        </View>

                        <Label style={{ color: airportRouteHeadingColor, fontSize: 16, fontWeight: '600', marginTop: 10 }}>{t('airport-route-connection')}</Label>
                        <View style={{ gap: 12 }}>
                          {airportZonesLoading && (
                            <View style={{ paddingVertical: 6 }}>
                              <Loading />
                            </View>
                          )}
                          {!airportZonesLoading &&
                            airportConnectionRouteOptions.map((option) => {
                              const selectedOption = airportConnectionDestination === option;
                              const optionId = option === AIRPORT_CONNECTION_NONE ? AIRPORT_CONNECTION_NONE : option;
                              return (
                                <Pressable
                                  key={`airport-connection-${optionId}`}
                                  onPress={() => {
                                    setAirportConnectionDestination(option);
                                    setAirportRouteError(undefined);
                                    setAirportApiError(undefined);
                                  }}
                                  style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}
                                >
                                  <View
                                    style={{
                                      width: 21,
                                      height: 21,
                                      borderRadius: 10.5,
                                      borderWidth: 1.5,
                                      borderColor: airportRouteRadioBorderColor,
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: airportRouteFieldStyle.backgroundColor,
                                    }}
                                  >
                                    {selectedOption && (
                                      <View style={{ width: 11, height: 11, borderRadius: 5.5, backgroundColor: airportRouteRadioFillColor }} />
                                    )}
                                  </View>
                                  <Label style={{ color: airportRouteTextColor, fontSize: 16, fontWeight: selectedOption ? '700' : '400' }}>
                                    {getAirportRouteLabel(option, true)}
                                  </Label>
                                </Pressable>
                              );
                            })}
                        </View>

                        {!!(airportRouteError || airportApiError) && <Label style={{ color: airportRouteErrorColor }}>{airportRouteError || airportApiError}</Label>}

                        <View style={{ alignItems: 'center', marginTop: 12 }}>
                          <CustomButton
                            title={t('confirm')}
                            style={{ width: 136, minHeight: 46, backgroundColor: airportRoutePrimaryButtonColor, borderColor: airportRoutePrimaryButtonColor }}
                            textStyle={{ color: '#ffffff', fontSize: 16, fontWeight: '700' }}
                            disabled={airportZonesLoading || airportMissionsLoading}
                            onPress={async () => {
                              const validationError = getAirportRouteValidationError();
                              if (validationError) {
                                setAirportRouteError(validationError);
                                return;
                              }

                              setAirportRouteError(undefined);
                              setAirportApiError(undefined);
                              setAirportSelectedTagIds([]);
                              await fetchAirportMissions({ selectedTagIds: [], openListOnSuccess: true });
                            }}
                          />
                        </View>
                      </View>
                    </View>
                  </ScrollView>
                  {showAirportRouteScrollHint && (
                    <Pressable
                      accessibilityRole="button"
                      onPress={handleAirportRouteScrollHintPress}
                      hitSlop={10}
                      style={{
                        position: 'absolute',
                        right: 16,
                        bottom: 16,
                        width: 40,
                        height: 40,
                        borderRadius: 20,
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#ffffff',
                        borderWidth: 1,
                        borderColor: airportRouteFieldStyle.borderColor,
                        opacity: 0.96,
                      }}
                    >
                      <FontAwesomeIcon icon={faChevronDown} size={14} color={airportRouteHeadingColor} />
                    </Pressable>
                  )}
                </View>
              </View>
            )}
            <View style={{ width: isWeb ? '100%' : contentWidth, flex: 1, display: isWeb && airportFlowStep !== 'list' ? 'none' : 'flex' }}>
              <View style={{ flex: 1, backgroundColor: airportListBackgroundColor }}>
                <View style={{ paddingHorizontal: 12, paddingTop: 8, paddingBottom: 4 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Pressable
                      onPress={() => {
                        openAirportStepBeforeList();
                      }}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 6,
                        paddingVertical: 4,
                      }}
                    >
                      <View
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: 10,
                          backgroundColor: airportListAccentColor,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} color={'#ffffff'} size={11} />
                      </View>
                      <Label style={{ color: airportListAccentColor, fontSize: 16, fontWeight: '700' }}>{t('back')}</Label>
                    </Pressable>
                  </View>
                  <View style={{ marginTop: 6, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <Label variant={LabelType.titleLargeNoCapsPrimary} style={{ color: airportListTitleColor }}>
                      {t('airport-list-title')}
                    </Label>
                    <MoreInfoButton title={''} size={22} color={airportListAccentColor} openURL={'missions-lanla-website-info-link'}>
                      {`${t('browse-through-the-available-missions')}`}
                    </MoreInfoButton>
                  </View>
                  <View
                    style={{
                      marginTop: 8,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: airportTripBannerBorderColor,
                      backgroundColor: airportTripBannerBackgroundColor,
                      padding: 10,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: airportTripBannerHeaderBackgroundColor,
                        borderRadius: 6,
                        paddingHorizontal: 8,
                        paddingVertical: 6,
                      }}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <View
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: 4,
                            backgroundColor: airportTripBannerTileBackgroundColor,
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Image source={airportTripPlaneIcon} style={{ width: 20, height: 20, tintColor: airportListImageIconColor }} resizeMode="contain" />
                        </View>
                        <Label style={{ color: airportListAccentColor, fontSize: 14, fontWeight: '700' }}>
                          {airportVisitType === ApiAirportVisitType.Departing ? t('airport-list-your-trip') : t('airport-list-your-airport-visit')}
                        </Label>
                      </View>
                      <Pressable
                        onPress={() => {
                          openAirportStepBeforeList();
                        }}
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 4,
                          backgroundColor: airportTripBannerTileBackgroundColor,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Image source={airportTripEditIcon} style={{ width: 16, height: 16, tintColor: airportListImageIconColor }} resizeMode="contain" />
                      </Pressable>
                    </View>
                    {airportVisitType === ApiAirportVisitType.Departing ? (
                      <View style={{ flexDirection: 'row', marginTop: 8 }}>
                        <View style={{ flex: 1 }}>
                          <Label style={{ color: airportListAccentColor, fontSize: 11, fontWeight: '700' }}>{t('airport-list-date')}</Label>
                          <Label style={{ color: airportTripBannerTextColor, fontSize: 12 }}>{airportDepartureDate || '-'}</Label>
                        </View>
                        <View style={{ flex: 1 }}>
                          <Label style={{ color: airportListAccentColor, fontSize: 11, fontWeight: '700' }}>{t('airport-list-destination')}</Label>
                          <Label style={{ color: airportTripBannerTextColor, fontSize: 12 }}>{airportDestinationLabel}</Label>
                        </View>
                        <View style={{ flex: 1 }}>
                          <Label style={{ color: airportListAccentColor, fontSize: 11, fontWeight: '700' }}>{t('airport-list-connection')}</Label>
                          <Label style={{ color: airportTripBannerTextColor, fontSize: 12 }}>{airportConnectionLabel}</Label>
                        </View>
                      </View>
                    ) : (
                      <View style={{ flexDirection: 'row', marginTop: 8 }}>
                        <View style={{ flex: 1 }}>
                          <Label style={{ color: airportListAccentColor, fontSize: 11, fontWeight: '700' }}>
                            {t('airport-list-visit-type')}
                          </Label>
                          <Label style={{ color: airportTripBannerTextColor, fontSize: 12 }}>{getAirportVisitTypeLabel(airportVisitType)}</Label>
                        </View>
                        <View style={{ flex: 1 }}>
                          <Label style={{ color: airportListAccentColor, fontSize: 11, fontWeight: '700' }}>
                            {getAirportPurposeDateLabel()}
                          </Label>
                          <Label style={{ color: airportTripBannerTextColor, fontSize: 12 }}>{airportPurposeDate || '-'}</Label>
                        </View>
                        <View style={{ flex: 1 }}>
                          <Label style={{ color: airportListAccentColor, fontSize: 11, fontWeight: '700' }}>{getAirportPurposeTimeLabel()}</Label>
                          <Label style={{ color: airportTripBannerTextColor, fontSize: 12 }}>{airportPurposeTime || '-'}</Label>
                        </View>
                      </View>
                    )}
                  </View>
                </View>

                {shouldShowAirportFilter && (
                  <View
                    style={{
                      paddingHorizontal: 12,
                      paddingTop: 0,
                      paddingBottom: 20,
                      position: 'relative',
                      zIndex: airportFilterOpen ? 60 : 2,
                      elevation: airportFilterOpen ? 60 : 2,
                    }}
                  >
                    <MapFilter
                      items={airportFilterItems}
                      showMissionTypeFilters={false}
                      onlyAcceptableMissions={airportOnlyAcceptableMissions}
                      showUpcomingMissions={airportShowUpcomingMissions}
                      keepSwitchesOnOneRow={true}
                      inlineMode={true}
                      switchesBottomSpacing={10}
                      unavailableToggleLabel={t('show-unavailable-short')}
                      upcomingToggleLabel={t('show-upcoming-short')}
                      filterBarBackgroundColor={airportListCardBackgroundColor}
                      filterBarTextColor={airportListAccentColor}
                      filterButtonIconColor={airportListAccentColor}
                      tagPopupBackgroundColor={airportListCardBackgroundColor}
                      tagPopupBorderColor={airportListBorderColor}
                      tagPopupTitleColor={airportListAccentColor}
                      tagPopupTextColor={airportListTextColor}
                      switchCardBackgroundColor={airportListCardBackgroundColor}
                      switchCardTextColor={airportListTextColor}
                      switchCardBorderColor={airportListBorderColor}
                      requireConfirmation={true}
                      onChangeText={() => {}}
                      onClear={() => {}}
                      onFocus={() => {}}
                      onBlur={() => {}}
                      onOpenChange={setAirportFilterOpen}
                      onKeyPress={() => {}}
                      onBack={() => {}}
                      onSelectionChange={(selectedItems) => {
                        setAirportApiError(undefined);
                        setAirportSelectedTagIds(selectedItems);
                        setAirportFilterItems((prev) =>
                          prev.map((item) => ({
                            ...item,
                            checked: selectedItems.includes(item.id),
                          }))
                        );
                        void fetchAirportMissions({ selectedTagIds: selectedItems });
                      }}
                      onSearchAreaPress={() => {}}
                      showSearchArea={false}
                      open={airportFilterOpen}
                      loading={airportMissionsLoading}
                      onConfirmSelection={({ selectedItems, onlyAcceptableMissions, showUpcomingMissions }) => {
                        const togglesChanged = airportOnlyAcceptableMissions !== onlyAcceptableMissions || airportShowUpcomingMissions !== showUpcomingMissions;

                        setAirportApiError(undefined);
                        setAirportSelectedTagIds(selectedItems);
                        setAirportFilterItems((prev) =>
                          prev.map((item) => ({
                            ...item,
                            checked: selectedItems.includes(item.id),
                          }))
                        );

                        if (togglesChanged) {
                          skipNextAirportToggleRefreshRef.current = true;
                        }

                        setAirportOnlyAcceptableMissions(onlyAcceptableMissions);
                        setAirportShowUpcomingMissions(showUpcomingMissions);

                        void fetchAirportMissions({
                          selectedTagIds: selectedItems,
                          onlyAcceptableMissions,
                          showUpcomingMissions,
                        });
                      }}
                      onOnlyAcceptableMissions={() => {
                        setAirportApiError(undefined);
                        setAirportOnlyAcceptableMissions((prev) => !prev);
                      }}
                      onShowUpcomingMissions={() => {
                        setAirportApiError(undefined);
                        setAirportShowUpcomingMissions((prev) => !prev);
                      }}
                    />
                  </View>
                )}

                {!!airportApiError && (
                  <View style={{ paddingHorizontal: 12, paddingBottom: 6 }}>
                    <Label style={{ color: airportListErrorColor }}>{airportApiError}</Label>
                  </View>
                )}
                <View style={{ flex: 1, position: 'relative' }}>
                  <FlatList
                    style={{ flex: 1 }}
                    data={filteredAirportMissionCardsForDisplay}
                    keyExtractor={(cardProps) => `airport-result-${cardProps.id}`}
                    contentContainerStyle={filteredAirportMissionCardsForDisplay.length <= 0 && !airportMissionsLoading ? { flexGrow: 1 } : undefined}
                    refreshControl={missionListRefreshControl}
                    keyboardShouldPersistTaps="handled"
                    nestedScrollEnabled={true}
                    initialNumToRender={10}
                    maxToRenderPerBatch={10}
                    windowSize={6}
                    updateCellsBatchingPeriod={16}
                    removeClippedSubviews={Platform.OS !== 'web'}
                    ListEmptyComponent={
                      !airportMissionsLoading ? (
                        <View style={{ flexGrow: 1, padding: 16, paddingTop: 16, alignItems: 'center', justifyContent: 'center', gap: 24 }}>
                          <FontAwesomeIcon icon={faFilterSlash} size={50} color={airportListAccentColor} />
                          <Label numberOfLines={5} style={{ color: airportListAccentColor, fontSize: 14 }}>
                            {t('no-mission-filter')}
                          </Label>
                        </View>
                      ) : null
                    }
                    renderItem={({ item: cardProps }) => (
                      <Pressable
                        onPress={() => {
                          cardProps.onPress?.(cardProps);
                        }}
                        style={{
                          backgroundColor: 'transparent',
                          borderBottomWidth: 1,
                          borderColor: airportListBorderColor,
                          paddingHorizontal: 12,
                          paddingVertical: 12,
                          flexDirection: 'row',
                          alignItems: 'center',
                          opacity: getMissionDiscoveryAvailability(cardProps) === false ? 0.5 : 1,
                        }}
                      >
                        <View
                          style={{
                            width: 50,
                            height: 50,
                            borderRadius: 25,
                            backgroundColor: airportListLogoBackgroundColor,
                            borderWidth: 1,
                            borderColor: airportListBorderColor,
                            overflow: 'hidden',
                            marginRight: 10,
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {cardProps.logoUrl ? (
                            <Image source={{ uri: cardProps.logoUrl }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
                          ) : (
                            <View style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: airportListCardInnerBackgroundColor }} />
                          )}
                        </View>
                        <View style={{ flex: 1 }}>
                          <Label style={{ color: airportListTextColor, fontSize: 20, fontWeight: '700' }}>{cardProps.title}</Label>
                          <Label style={{ color: airportListTextColor, fontSize: 15 }}>{getAirportMissionZoneLabel(cardProps)}</Label>
                          <Label style={{ color: airportListAccentColor, fontSize: 14, fontWeight: '600' }}>
                            {cardProps.industry?.split(',')?.[0] || t('restaurant_button')}
                          </Label>
                        </View>
                        <View style={{ alignItems: 'flex-end', justifyContent: 'flex-start', minHeight: 44, marginLeft: 10, alignSelf: 'flex-start' }}>
                          <Label style={{ color: airportListAccentColor, fontSize: 9, fontWeight: '700', lineHeight: 10, marginBottom: 2 }}>
                            {t('airport-list-gate')}
                          </Label>
                          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <Image source={airportGateIcon} style={{ width: 18, height: 18, tintColor: airportListImageIconColor }} resizeMode="contain" />
                            <Label style={{ color: airportListAccentColor, fontSize: 12, fontWeight: '700', marginLeft: 2 }}>
                              {cardProps.branchAirportInfo?.gateName || '-'}
                            </Label>
                          </View>
                        </View>
                      </Pressable>
                    )}
                  />
                  {airportMissionsLoading && (
                    <View
                      pointerEvents="none"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: `${airportListBackgroundColor}DD`,
                      }}
                    >
                      <Loading />
                    </View>
                  )}
                </View>
              </View>
            </View>
          </ScrollView>
        ) : (
          mainMissionList
        )}
      </View>
    </View>
  );
};

export default MissionList;
